import type { DocMigration } from "../docMigration";

/**
 * Extracts the flat coordinate array from a perimeter that may be a
 * Float64Array (post-unmarshal) or a raw JSON object with `.values`.
 */
function getPerimeterCoords(perimeter: any): ArrayLike<number> | undefined {
  if (perimeter instanceof Float64Array) {
    return perimeter;
  }
  if (perimeter?.values && typeof perimeter.values.length === "number") {
    return perimeter.values;
  }
  return undefined;
}

function signedArea(pl: ArrayLike<number>): number {
  let sum = 0;
  for (let i = 0, l = pl.length - 2; i < l; i += 2) {
    sum += (pl[i + 2] - pl[i]) * (pl[i + 1] + pl[i + 3]);
  }
  return sum / 2;
}

/** Returns a reversed copy of a closed polyline's coordinate array. */
function reverseCoordsArray(coords: ArrayLike<number>): number[] {
  const vertexCount = coords.length / 2 - 1;
  const out: number[] = new Array(coords.length);
  for (let i = 0; i < vertexCount; i++) {
    const src = (vertexCount - 1 - i) * 2;
    out[i * 2] = coords[src];
    out[i * 2 + 1] = coords[src + 1];
  }
  // close the loop
  out[vertexCount * 2] = out[0];
  out[vertexCount * 2 + 1] = out[1];
  return out;
}

/**
 * Finds a guaranteed interior point by iterating convex ear triangles on a
 * CCW-wound polygon. Mirrors computeConvexVertexInteriorPoint from
 * geom/containment.ts — actually reverses the array when CW so that
 * consecutive vertex triples are true ears.
 */
function computeInteriorPoint(coords: ArrayLike<number>): [number, number] {
  const vertexCount = coords.length / 2 - 1;
  if (vertexCount < 3) {
    return [0, 0];
  }

  const ccw = signedArea(coords) > 0 ? reverseCoordsArray(coords) : coords;

  for (let i = 0; i < vertexCount; i++) {
    const i0 = i * 2;
    const i1 = ((i + 1) % vertexCount) * 2;
    const i2 = ((i + 2) % vertexCount) * 2;

    const ax = ccw[i1] - ccw[i0];
    const ay = ccw[i1 + 1] - ccw[i0 + 1];
    const bx = ccw[i2] - ccw[i1];
    const by = ccw[i2 + 1] - ccw[i1 + 1];

    // cross product > 0 ⇒ convex (left-turn) vertex in CCW winding
    if (ax * by - ay * bx <= 0) {
      continue;
    }

    return [(ccw[i0] + ccw[i1] + ccw[i2]) / 3, (ccw[i0 + 1] + ccw[i1 + 1] + ccw[i2 + 1]) / 3];
  }

  // Fallback: simple centroid
  let sx = 0;
  let sy = 0;
  for (let i = 0; i < vertexCount; i++) {
    sx += coords[i * 2];
    sy += coords[i * 2 + 1];
  }
  return [sx / vertexCount, sy / vertexCount];
}

const migration: DocMigration = {
  id: "20260427_add_floor_split_data",
  description: "Add splitData with one bay per perimeter to FREE_FORM floors that lack it",
  apply(json: any): any {
    const floors: Record<string, any> | undefined = json.floors;
    if (!floors) {
      return json;
    }

    for (const floor of Object.values(floors)) {
      if (!floor || typeof floor !== "object") {
        continue;
      }
      if (floor.horizontalAssemblyType !== "FREE_FORM") {
        continue;
      }
      if (floor.splitData) {
        continue;
      }

      const perimeters: any[] = floor.perimeters ?? [];

      if (perimeters.length === 0) {
        continue;
      }

      const bays: Record<string, any> = {};

      for (let pi = 0; pi < perimeters.length; pi++) {
        const coords = getPerimeterCoords(perimeters[pi]);
        if (!coords || coords.length < 6) {
          continue;
        }
        const interiorPoint = computeInteriorPoint(coords);
        const bayId = `${floor.id}-bay-${pi}`;
        bays[bayId] = { id: bayId, interiorPoint };
      }

      floor.splitData = { splitLines: {}, bays };
    }

    return json;
  },
};

export default migration;
